import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import i18n from '../../utilities/i18n';
import {
  IconButton,
  IconNavProfileOutlined,
  Text,
  Popup,
  VStack,
} from '@adc/parallax-component-library';
import { LogOut as IconLogOut } from '@tamagui/lucide-icons';
import { useAppSelector } from '../../reducers/hooks';
import mediator from '../../utilities/mediator';
import { User } from '../../models';
import { ChangeOrganizationModal } from '../modal/ChangeOrganizationModal';
import { useQuery } from '@tanstack/react-query';
import {
  organizationQueryOptions,
  organizationsQueryOptions,
} from '../../queryOptions';
import './UserMenu.scss';

export type MenuItem = {
  label: React.JSX.Element;
  icon?: React.JSX.Element;
  action?: () => void;
};

export type MenuItemGroup = {
  label: string;
  children: MenuItem[];
};

export default function UserMenu() {
  const navigate = useNavigate();

  const user: User = useAppSelector((state) => state.user);
  const logout = () => {
    mediator.publishAndWait('auth:logout');
    navigate('/');
  };

  const [changeOrganizationModalOpen, setChangeOrganizationModalOpen] =
    useState(false);

  const organizationKey = useAppSelector(
    (state) => state.organizations.currentOrgKey,
  );

  const organizations = useQuery(organizationsQueryOptions());

  const organization = useQuery(
    organizationQueryOptions({ key: organizationKey }),
  );

  const userMenuItems: (MenuItem | MenuItemGroup)[] = [
    {
      label: i18n.t('UserMenu.account'),
      children: [
        {
          label: (
            <Text
              alignStart={true}
              type="$bodySmall.medium"
            >{`${user.firstName} ${user.lastName}`}</Text>
          ),
        },
        {
          label: (
            <Text alignStart={true} type="$bodySmall.default">
              {user.email}
            </Text>
          ),
        },
      ],
    },
    {
      label: i18n.t('UserMenu.organization'),
      children: [
        {
          label: (
            <Text
              alignStart={true}
              type="$bodySmall.default"
              color={organization.data?.name ? '$text.100' : '$text.60'}
            >
              {organization.data?.name || i18n.t('Common.noData')}
            </Text>
          ),
        },
      ],
    },
    {
      label: (
        <Text alignStart={true} type="$bodySmall.default" color="$text.80">
          {i18n.t('UserMenu.logout')}
        </Text>
      ),
      icon: <IconLogOut strokeWidth={1.5} color="$text.80" />,
      action: logout,
    },
  ];

  if ((organizations.data?.length ?? 0) > 1) {
    (userMenuItems[1] as MenuItemGroup).children.push({
      label: (
        <Text alignStart={true} type="$bodySmall.default">
          {i18n.t('UserMenu.changeOrganization')}
        </Text>
      ),
      action: () => setChangeOrganizationModalOpen(true),
    });
  }

  function renderMenuItem(item: MenuItem, index: number) {
    let onPress;

    if (item.action) {
      onPress = () => {
        item.action?.();
      };
    }

    return (
      <div
        className={`user-menu-item ${item.action ? 'clickable' : ''}`}
        role="menuitem"
        onClick={onPress}
        key={index}
      >
        {item.icon}
        {item.label}
      </div>
    );
  }

  return (
    <VStack theme="light">
      <ChangeOrganizationModal
        open={changeOrganizationModalOpen}
        onClose={() => setChangeOrganizationModalOpen(false)}
      />
      <Popup
        placement="bottom-end"
        content={
          <div className="user-menu">
            {userMenuItems.map((menuItem, index) => {
              if ('children' in menuItem) {
                return (
                  <div key={`group-${index}`} className="user-menu-item-group">
                    <div className="user-menu-item-group-label">
                      <Text type="$labelSmall.default" color="$text.60">
                        {menuItem.label}
                      </Text>
                    </div>
                    {menuItem.children.map((childItem, index) => {
                      return renderMenuItem(childItem, index);
                    })}
                  </div>
                );
              } else {
                return renderMenuItem(menuItem, index);
              }
            })}
          </div>
        }
      >
        <IconButton
          aria-label={i18n.t('UserMenu.profile')}
          onPress={() => {}}
          size="S"
          variant="primary"
          circle
          key="profile"
          theme="dark_onBrand"
        >
          <IconNavProfileOutlined color="$surface.brand" />
        </IconButton>
      </Popup>
    </VStack>
  );
}
